import {
  HabitatIntactnessAndThreats,
  LayerFactValue,
  MAP_LAYER_FACTS,
  R1FactType,
  RasterLayerYear,
} from '@/api/rest/resources/types/fact';
import { IUCN_STATIC_HASH } from '@/config/constants';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { WMS_PARAMS } from '@/pages/shared/plots/pages/hooks/useLayerUrl';
import { isNullish } from '@/utils/booleans';

import { LayerProps, MAP_ORIGIN } from '../constants';

export const useMapEndpointPlot = (
  layer: LayerProps | undefined,
  year: string,
  legendFilter?: string | null,
): string => {
  const value = layer?.queryFact ?? layer?.value ?? R1FactType.unknown;
  const plotFacts = usePlotFacts<string | LayerFactValue>([value]);

  const factValue = getFactFromFactArray<string | LayerFactValue>(plotFacts, value)?.value;

  const layerName = layer?.value ?? '';
  const layerOrigin = layer?.origin || MAP_ORIGIN.cckp;

  const hasUrlFromFact = layerOrigin === MAP_ORIGIN.fact && !isNullish(factValue);
  const isHabitatIntactnessLayer = layerName === MAP_LAYER_FACTS.habitat_intactness_raster_urls;
  const isTimeSeries = layer?.isTimeSeries;

  const filter = legendFilter ? `&CQL_FILTER=${legendFilter}` : '';

  if (hasUrlFromFact && isHabitatIntactnessLayer) {
    const rasterLayerType = layer?.layerSubType as HabitatIntactnessAndThreats;
    const baseUrl = (factValue as LayerFactValue)?.[`${rasterLayerType}`]?.[year as RasterLayerYear]?.map_url;

    return baseUrl ? `${baseUrl}${WMS_PARAMS}${filter}` : '';
  }

  if (hasUrlFromFact) {
    const baseUrl = isTimeSeries ? (factValue as Record<string, string>)?.[`${year}`] : (factValue as string);
    return baseUrl ? `${baseUrl}${WMS_PARAMS}${filter}` : '';
  }

  return getMapEndpoint(layerOrigin, layerName, year, filter);
};

export const useMapEndpointOrg = (
  layer: LayerProps | undefined,
  year: string,
  legendFilter?: string | null,
): string => {
  const value = layer?.queryFact ?? layer?.value ?? R1FactType.unknown;
  const factValue = useOrganizationIndicatorReportingFact<string>(value);

  const origin = layer?.origin || MAP_ORIGIN.cckp;
  const layerName = layer?.value ?? '';

  const hasUrlFromFact = origin === MAP_ORIGIN.fact && !isNullish(factValue) && factValue?.[`${value}`];
  const isHabitatIntactnessLayer = layerName === MAP_LAYER_FACTS.habitat_intactness_raster_urls;

  const filter = legendFilter ? `&CQL_FILTER=${legendFilter}` : '';

  if (hasUrlFromFact && !isHabitatIntactnessLayer) {
    return factValue?.[`${value}`]?.value ?? '';
  }

  if (hasUrlFromFact && isHabitatIntactnessLayer) {
    const rasterLayerType = layer?.layerSubType as HabitatIntactnessAndThreats;
    const layerFactValue = factValue?.[`${value}`]?.value as LayerFactValue;
    const baseUrl = layerFactValue?.[`${rasterLayerType}`]?.[year as RasterLayerYear]?.map_url;
    return baseUrl ? `${baseUrl}${WMS_PARAMS}${filter}` : '';
  }

  return getMapEndpoint(origin, layerName, year, filter);
};

const getMapserverBaseUrl = (origin: string, layer: string) => {
  if (origin === MAP_LAYER_FACTS.unknown) {
    return '';
  }
  if (origin === MAP_ORIGIN.gbif) {
    return 'https://api.gbif.org/v2/map/occurrence/density/{z}/{x}/{y}@1x.png';
  }
  // SUPER HACK: to make iucn url unguessable. Hash to be removed when keycloak is setup on geoserver
  if (layer === MAP_LAYER_FACTS.iucn_protected_areas) {
    return `https://mapserver.landler.io/geoserver/${origin}/wms?service=WMS&version=1.1.0&request=GetMap&layers=${origin}:${layer}_${IUCN_STATIC_HASH}`;
  }

  return `https://mapserver.landler.io/geoserver/${origin}/wms?service=WMS&version=1.1.0&request=GetMap&layers=${origin}:${layer}`;
};

export const getMapEndpoint = (origin: string, layer: string, year: string, filter = '') => {
  const baseUrl = getMapserverBaseUrl(origin, layer);
  const baseURLParams = `format=image/png&srs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256${filter}`;

  if (origin === MAP_ORIGIN.gbif) {
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    return `${baseUrl}?style=scaled.circles&srs=EPSG:3857&basisOfRecord=HUMAN_OBSERVATION&basisOfRecord=MACHINE_OBSERVATION&basisOfRecord=OBSERVATION&basisOfRecord=OCCURRENCE&${year ? `&year=${year}` : ''}${filter}`;
  }

  if (origin === MAP_ORIGIN.terrascope) {
    return `${baseUrl}_2021&${baseURLParams}`;
  }

  if (layer === MAP_LAYER_FACTS.dw_lulc) {
    return `${baseUrl}_${year}&${baseURLParams}`;
  }

  if (
    [MAP_ORIGIN.population_density, MAP_ORIGIN.esa].includes(origin as string as typeof MAP_ORIGIN.population_density)
  ) {
    return `${baseUrl}_${year}&transparent=true&${baseURLParams}`;
  }

  const yearFilter = year ? `&time=${year}` : '';
  return `${baseUrl}${yearFilter}&transparent=true&${baseURLParams}`;
};
